import React, { useMemo, useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { Map, Plus, Info, Star, Trash2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { CareerCard } from "./CareerCard";
import { MilestoneDialog, VisionBlock } from "./RoadmapCard";
import { MILESTONE_STATUSES } from "@/lib/careerConfig";
import { cn } from "@/lib/utils";
import { translateFr as tfr } from "@/i18n";

const BUCKETS = [
  { id: 0, label: tfr("Aujourd'hui"), max: 0 },
  { id: 6, label: "6 mois", max: 6 },
  { id: 12, label: "12 mois", max: 12 },
  { id: 18, label: "18 mois", max: 18 },
  { id: 24, label: "24 mois", max: 24 },
  { id: 36, label: "36 mois+", max: 999 },
];

const STATE = {
  completed: { chip: "border-success/40 bg-success/10", dot: "bg-success", label: "Objectif atteint" },
  in_progress: { chip: "border-athlynx/40 bg-athlynx/10", dot: "bg-athlynx", label: tfr("En cours") },
  upcoming: { chip: "border-border bg-secondary/40", dot: "bg-muted-foreground/50", label: tfr("À venir") },
  at_risk: { chip: "border-warning/40 bg-warning/10", dot: "bg-warning", label: tfr("À risque") },
  delayed: { chip: "border-danger/40 bg-danger/10", dot: "bg-danger", label: tfr("Retardé") },
};

function MilestoneDetail({ m, playerId, onChanged, onClose }) {
  const [title, setTitle] = useState(m.title);
  const [month, setMonth] = useState(String(m.target_month ?? 0));
  const [status, setStatus] = useState(m.status);
  const [isKey, setIsKey] = useState(!!m.is_key);
  const [saving, setSaving] = useState(false);

  const save = async () => {
    setSaving(true);
    try {
      await api.patch(`/players/${playerId}/career-plan/milestones/${m.id}`, {
        title: title.trim(), target_month: Number(month) || 0, status, is_key: isKey,
      });
      toast.success(tfr("Jalon mis à jour"));
      onClose(); onChanged?.();
    } catch { toast.error(tfr("Impossible de mettre à jour le jalon")); }
    finally { setSaving(false); }
  };
  const remove = async () => {
    try {
      await api.delete(`/players/${playerId}/career-plan/milestones/${m.id}`);
      toast.success(tfr("Jalon supprimé"));
      onClose(); onChanged?.();
    } catch { toast.error(tfr("Impossible de supprimer le jalon")); }
  };

  return (
    <Dialog open onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-md" data-testid="milestone-detail-dialog">
        <DialogHeader><DialogTitle>{tfr("Détail du jalon")}</DialogTitle></DialogHeader>
        <div className="space-y-4">
          <div className="space-y-1.5">
            <Label>Titre</Label>
            <Input value={title} onChange={(e) => setTitle(e.target.value)} maxLength={200} data-testid="ms-detail-title" />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1.5">
              <Label>{tfr("Échéance (mois)")}</Label>
              <Input type="number" min={0} max={120} value={month} onChange={(e) => setMonth(e.target.value)} data-testid="ms-detail-month" />
            </div>
            <div className="space-y-1.5">
              <Label>{tfr("Statut")}</Label>
              <Select value={status} onValueChange={setStatus}>
                <SelectTrigger data-testid="ms-detail-status"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {MILESTONE_STATUSES.map((s) => <SelectItem key={s.id} value={s.id}>{s.label}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
          </div>
          <label className="flex items-center gap-2 text-sm cursor-pointer">
            <Checkbox checked={isKey} onCheckedChange={(v) => setIsKey(!!v)} /> Jalon clé
          </label>
        </div>
        <DialogFooter className="flex-row justify-between sm:justify-between">
          <Button variant="ghost" size="sm" onClick={remove} className="text-danger gap-1" data-testid="ms-detail-delete">
            <Trash2 className="h-3.5 w-3.5" /> Supprimer
          </Button>
          <div className="flex gap-2">
            <Button variant="ghost" onClick={onClose}>{tfr("Annuler")}</Button>
            <Button onClick={save} disabled={saving || !title.trim()} data-testid="ms-detail-save">{tfr("Enregistrer")}</Button>
          </div>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export function VisionTimeline({ plan, milestones, playerId, onChanged }) {
  const [addOpen, setAddOpen] = useState(false);
  const [selected, setSelected] = useState(null);

  const buckets = useMemo(() => BUCKETS.map((b, i) => {
    const min = i === 0 ? -1 : BUCKETS[i - 1].max;
    return { ...b, items: milestones.filter((m) => (m.target_month ?? 0) > min && (m.target_month ?? 0) <= b.max) };
  }), [milestones]);

  return (
    <CareerCard icon={Map} title={tfr("Vision carrière")} subtitle={`Feuille de route sur ${plan.horizon_months} mois`} testid="vision-timeline-card"
      right={
        <Button variant="outline" size="sm" onClick={() => setAddOpen(true)} className="h-8 gap-1 text-xs" data-testid="timeline-add-milestone">
          <Plus className="h-3.5 w-3.5" /> Jalon
        </Button>
      }>
      <VisionBlock plan={plan} playerId={playerId} onChanged={onChanged} />
      {milestones.length === 0 ? (
        <div className="text-center py-8" data-testid="timeline-empty">
          <p className="text-sm text-muted-foreground">{tfr("Aucun jalon défini.")}</p>
          <Button variant="outline" size="sm" className="mt-3 gap-1" onClick={() => setAddOpen(true)}>
            <Plus className="h-3.5 w-3.5" /> Ajouter le premier jalon
          </Button>
        </div>
      ) : (
        <div className="overflow-x-auto snap-x pb-2 -mx-1 px-1" data-testid="timeline-scroll">
          <div className="min-w-[720px] grid grid-cols-6 gap-2">
            {buckets.map((b, i) => (
              <div key={b.id} className="snap-start">
                <div className="relative flex items-center mb-3">
                  <span className={cn("h-2.5 w-2.5 rounded-full shrink-0 z-10",
                    b.items.some((m) => m.status === "in_progress") ? "bg-athlynx" :
                    b.items.length && b.items.every((m) => m.status === "completed") ? "bg-success" : "bg-muted-foreground/40")} />
                  {i < buckets.length - 1 && <span className="absolute left-2 right-[-8px] h-px bg-border" />}
                </div>
                <p className={cn("text-[11px] font-semibold mb-2", i === 0 ? "text-athlynx" : "text-muted-foreground")}>{b.label}</p>
                <div className="space-y-1.5">
                  {b.items.map((m) => {
                    const st = STATE[m.status] || STATE.upcoming;
                    return (
                      <button key={m.id} onClick={() => setSelected(m)} data-testid={`timeline-ms-${m.id}`}
                        className={cn("w-full text-left rounded-lg border p-2 transition-colors hover:brightness-110", st.chip)}>
                        <div className="flex items-start gap-1.5">
                          <span className={cn("h-1.5 w-1.5 rounded-full mt-1 shrink-0", st.dot)} />
                          <div className="min-w-0">
                            <p className="text-[11px] font-medium leading-tight line-clamp-3">{m.title}</p>
                            <p className="text-[10px] text-muted-foreground mt-0.5 inline-flex items-center gap-1">
                              M+{m.target_month}
                              {m.is_key && <Star className="h-2.5 w-2.5 text-warning fill-current" aria-label={tfr("Jalon clé")} />}
                            </p>
                          </div>
                        </div>
                      </button>
                    );
                  })}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
      <div className="flex flex-wrap items-center gap-x-4 gap-y-1 mt-4 pt-3 border-t border-border" data-testid="timeline-legend">
        {[["bg-success", tfr("Objectif atteint")], ["bg-athlynx", tfr("En cours")], ["bg-muted-foreground/50", tfr("À venir")]].map(([dot, label]) => (
          <span key={label} className="inline-flex items-center gap-1.5 text-[11px] text-muted-foreground">
            <span className={cn("h-2 w-2 rounded-full", dot)} /> {label}
          </span>
        ))}
        <span className="inline-flex items-center gap-1.5 text-[11px] text-muted-foreground">
          <Star className="h-3 w-3 text-warning fill-current" /> Jalon clé
        </span>
        <span className="inline-flex items-center gap-1 text-[10px] text-muted-foreground/70 ml-auto">
          <Info className="h-3 w-3" /> Cliquez sur un jalon pour le modifier
        </span>
      </div>
      <MilestoneDialog open={addOpen} onOpenChange={setAddOpen} playerId={playerId} onChanged={onChanged} />
      {selected && <MilestoneDetail m={selected} playerId={playerId} onChanged={onChanged} onClose={() => setSelected(null)} />}
    </CareerCard>
  );
}
